import React, { useReducer, useContext, useLayoutEffect, useRef, useState } from 'react'
import axios from '@utils/webRequests'
import useAgeVerificationContext from './ageVerificationContext'
import { flushSync } from 'react-dom'
import snakecaseKeys from 'snakecase-keys'

const AUTH_START = 'AUTH_START'
const AUTH_SUCCESS = 'AUTH_SUCCESS'
const VALIDATE_START = 'VALIDATE_START'
const AUTH_FAIL = 'AUTH_FAIL'
const UPDATE_START = 'UPDATE_START'
const UPDATE_FAIL = 'UPDATE_FAIL'
const UPDATE_SUCCESS = 'UPDATE_SUCCESS'
const LOGOUT = 'LOGOUT'
const INVALID_TOKEN = 'INVALID_TOKEN'

const INITIAL_AUTHENTICATION_STATE = {
  FAILED_INITIAL_AUTH: Symbol('FAILED_INITIAL_AUTH'),
  SUCCEEDED_INITIAL_AUTH: Symbol('SUCCEEDED_INITIAL_AUTH'),
  PENDING_INITIAL_AUTH: Symbol('PENDING_INITIAL_AUTH'),
}

const initialState = {
  loading: false,
  authenticated: false,
  user: null,
  invalidToken: false,
  firstRender: true,
}

const reducer = (state, action) => {
  switch (action.type) {
    case AUTH_START:
      return {
        ...state,
        loading: true,
        authenticated: false
      }
    case AUTH_SUCCESS:
      return {
        ...state,
        loading: false,
        authenticated: true,
        user: action.user,
        invalidToken: false,
        firstRender: false,
      }
    case AUTH_FAIL:
      return {
        ...state,
        loading: false,
        authenticated: false,
        firstRender: false
      }
    case UPDATE_START:
      return { ...state, loading: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        loading: false,
        user: action.user,
        firstRender: false,
      }
    case UPDATE_FAIL:
      return {
        ...state,
        loading: false,
        firstRender: false
      }
    case VALIDATE_START:
      return { ...state, loading: true }
    case LOGOUT:
      return {
        ...initialState,
        firstRender: false,
      }
    case INVALID_TOKEN:
      return {
        ...state,
        loading: false,
        authenticated: false,
        invalidToken: true,
        firstRender: false
      }
    default:
      throw new Error('Unexpected action')
  }
}

const AuthContext = React.createContext()

const AuthProvider = ({ children }) => {
  const { PENDING_INITIAL_AUTH, SUCCEEDED_INITIAL_AUTH, FAILED_INITIAL_AUTH } = INITIAL_AUTHENTICATION_STATE
  const { ageValue, acceptedTerms, meetsCriteria } = useAgeVerificationContext()
  const [initialAuthentication, setInitialAuthentication] = useState(PENDING_INITIAL_AUTH)
  const [state, dispatch] = useReducer(reducer, initialState)
  const authStateRef = useRef(null)
  const AGE_OR_TERMS_NOT_MET = 'Age or terms not met'

  useLayoutEffect(() => {
    initialAuthentication === PENDING_INITIAL_AUTH
      ? validateTokenAndSetInitialAuth()
      : validateToken()
  }, [])

  // sets the state to a reference to be used when state is likely to be stale
  useLayoutEffect(() => {
    authStateRef.current = state
  }, [state])

  // We use the initial authentication to tell if we need to get the region/geo district for the user before login for regulatory compliance
  const validateTokenAndSetInitialAuth = () => {
    validateToken()
      .then(() => setInitialAuthentication(SUCCEEDED_INITIAL_AUTH))
      .catch(() => setInitialAuthentication(FAILED_INITIAL_AUTH))
  }

  const handleLogin = (user) => {
    dispatch({ type: AUTH_START })
    return new Promise((resolve, reject) => {
      axios
        .post('/api/auth/authentication', user)
        .then((res) => {
          dispatch({ type: AUTH_SUCCESS, user: res.data })
          resolve(res)
        })
        .catch((res) => {
          dispatch({ type: AUTH_FAIL })
          reject(res)
        })
    })
  }

  const handleThirdPartyLogin = (code, service) => {
    dispatch({ type: AUTH_START })
    return new Promise((resolve, reject) => {
      axios
        .post('/api/auth/third_party_authentication', { code, service })
        .then((res) => {
          dispatch({ type: AUTH_SUCCESS, user: res.data })
          resolve(res)
        })
        .catch((res) => {
          dispatch({ type: AUTH_FAIL })
          reject(res)
        })
    })
  }

  const handleLogout = () => {
    dispatch({ type: LOGOUT })
    localStorage.clear()
    sessionStorage.clear()
    return axios.delete('/api/auth/authentication')
  }
  
  const handleInvalidateTokens = () => {
    return axios.delete('/api/auth/invalidate_tokens')
      .then(() => {
        dispatch({ type: LOGOUT })
        localStorage.clear()
      })
  }

  const handleDestroyAccount = () => {
    return axios.delete('/api/auth/accounts')
      .then(() => {
        dispatch({ type: LOGOUT })
        localStorage.clear()
      })
  }

  const handleSendPasswordReset = () => {
    return axios.post('/api/auth/password_resets')
  }

  const handleConfirmPasswordReset = (data) => {
    return axios.put('/api/auth/password_resets', data)
  }

  const handleInvalidToken = () => {
    dispatch({ type: INVALID_TOKEN })
  }

  const logStatus = () => {
    console.log(`Age Criteria is met: ${meetsCriteria}`)
    console.log(`Age is Confirmed: ${acceptedTerms}`)
    console.log(`registering user with age: ${JSON.stringify(ageValue, null, 2)}`)
  }

  const validateUserMeetsCriteria = () => {
    if (acceptedTerms && meetsCriteria) {
      logStatus()
      return true
    } else {
      console.error(AGE_OR_TERMS_NOT_MET)
      return false
    }
  }

  const handleEmailPassRegister = (user) => {
    if (!validateUserMeetsCriteria()) return;
    // navigator.geolocation.getCurrentPosition((position) => {
    //   const { longitude, latitude } = position.coords || {}

    // })
    dispatch({ type: AUTH_START })
    let dateOfBirth = new Date(`${ageValue.month} ${ageValue.day} ${ageValue.year}`).toISOString()
    let isTermsAccepted = acceptedTerms
    const data = snakecaseKeys({ ...user, dateOfBirth, isTermsAccepted })
    return axios.post('/api/auth/register_users', data)
      .then((res) => {
        console.log(res.data)
        dispatch({ type: AUTH_SUCCESS, user: res.data })
      })
      .catch((err) => {
        dispatch({ type: AUTH_FAIL })
      })
  }

  const handleAnonymousRegister = () => {
    if (!validateUserMeetsCriteria()) return;
    // navigator.geolocation.getCurrentPosition((position) => {
    //   const { longitude, latitude } = position.coords || {}

    // })
    dispatch({ type: AUTH_START })
    let dateOfBirth = new Date(`${ageValue.month} ${ageValue.day} ${ageValue.year}`).toISOString()
    let isTermsAccepted = acceptedTerms
    const data = snakecaseKeys({ dateOfBirth, isTermsAccepted })
    return axios.post('/api/auth/anonymous_users', data)
      .then((res) => {
        dispatch({ type: AUTH_SUCCESS, user: res.data })
      })
      .catch((res) => {
        dispatch({ type: AUTH_FAIL })
      })
  }

  const handleThirdPartyRegister = (code, service) => {
      // axios
      //   .post('/api/auth/register_users', user)
      //   .then((res) => {
      //     dispatch({ type: AUTH_SUCCESS, user: res.data })
      //     resolve(res)
      //   })
      //   .catch((res) => {
      //     dispatch({ type: AUTH_FAIL })
      //     reject(res)
      //   })
  }

  // const handleUpdate = (user) => {
  //   dispatch({ type: UPDATE_START })
  //   const isAdmin = state.user.role === 'admin'
  //   const url = isAdmin ? '/api/auth/admin_update' : '/api/auth/user'
  //   return new Promise((resolve, reject) => {
  //     axios
  //       .put(url, user)
  //       .then((res) => {
  //         dispatch({ type: UPDATE_SUCCESS, user: res.data })
  //         resolve(res)
  //       })
  //       .catch((res) => {
  //         dispatch({ type: UPDATE_FAIL })
  //         reject(res)
  //       })
  //   })
  // }

  const handleInstantUpdate = (user) => {
    dispatch({ type: UPDATE_SUCCESS, user, })
  }

  const handleNearbyUpdate = (data) => {
    dispatch({ type: UPDATE_START })
    return new Promise((resolve, reject) => {
      axios
        .put(`/api/my/profiles/nearby_profile`, data)
        .then((res) => {
          dispatch({ type: UPDATE_SUCCESS, user: res.data })
          resolve(res)
        })
        .catch((res) => {
          dispatch({ type: UPDATE_FAIL })
          reject(res)
        })
    })
  }

  function validateToken() {
    dispatch({ type: VALIDATE_START })
    return new Promise((resolve, reject) => {
      axios
        .post('/api/auth/validate_token')
        .then((res) => {
          dispatch({ type: AUTH_SUCCESS, user: res.data })
          resolve(res)
        })
        .catch((res) => {
          dispatch({ type: AUTH_FAIL })
          reject(res)
        })
    })
  }

  const getUser = () => state.user

  return (
    <AuthContext.Provider
      value={{
        state,
        authStateRef,
        dispatch,
        login: handleLogin,
        thirdPartyLogin: handleThirdPartyLogin,

        registerEmailPass: handleEmailPassRegister,
        registerAnonymous: handleAnonymousRegister,
        registerThirdParty: handleThirdPartyRegister,

        // update: handleUpdate,
        handleNearbyUpdate,
        handleInstantUpdate,

        logout: handleLogout,
        destroyAccount: handleDestroyAccount,
        invalidateTokens: handleInvalidateTokens,
        invalidateToken: handleInvalidToken,
        validateToken,

        sendPasswordReset: handleSendPasswordReset,
        confirmPasswordReset: handleConfirmPasswordReset,

        authenticated: state.authenticated,
        userLoading: state.loading,
        showHiddenDisplay: state?.user?.isHidden && !state?.user?.isSubscribed,
        getUser,

        initialAuthentication,
        INITIAL_AUTHENTICATION_STATE,
        SUCCEEDED_INITIAL_AUTH,
        FAILED_INITIAL_AUTH,
        PENDING_INITIAL_AUTH,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthConsumer = AuthContext.Consumer

const useAuthContext = () => {
  const contextValue = useContext(AuthContext)
  return contextValue
}

function withAuthContext(Component) {
  return function WrapperComponent(props) {
    return (
      <AuthConsumer>
        {(state) => <Component {...props} authContext={state} />}
      </AuthConsumer>
    )
  }
}

export {
  AuthContext,
  AuthConsumer,
  AuthProvider,
  withAuthContext,
  useAuthContext,
}